import { Box, ListItem, PopperProps, Radio } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { DEFAULT_FONTSIZE } from "../utils/utils";
import { CHART_DIMENSIONS } from "./app/provider/patient/chart/tokens";
import Flex, { FlexProps } from "./Flex";
import { inputLabel } from "./MaskInput";
import Text from "./Text";

type AsyncFilterSelectOption = { [k: string]: string };

interface AsyncFilterSelectProps<T extends AsyncFilterSelectOption> {
  label?: string;
  options?: T[];
  loading?: boolean;
  value?: T | null;
  onChange: (newValue: T | null) => void;
  formatKeys?: { val: string; label: string };
  width?: number;
  onSearch?: (v: string) => void;
  flexProps?: FlexProps;
  required?: boolean;
  error?: string | boolean;
  handleBlur?: any;
  placeholder?: string;
  name?: string;
  enableFilterOption?: boolean;
  disabled?: boolean;
  renderCustomLabel?: (opt: AsyncFilterSelectOption) => React.ReactNode;
  renderSelectedValue?: (opt: AsyncFilterSelectOption) => React.ReactNode;
  disableSortOptions?: boolean;
  forcePopupIcon?: boolean;
  CustomPopperComponent?: React.ComponentType<PopperProps>;
  fullHeight?: boolean;
  gutterTop?: boolean;
  endIcon?: React.ReactNode;
  clearable?: boolean;
}

function AsyncFilterSelect<T extends AsyncFilterSelectOption>({
  label,
  options = [],
  loading = false,
  value = null,
  onChange,
  formatKeys = { val: "value", label: "label" },
  flexProps,
  onSearch,
  required,
  error,
  placeholder = "Select",
  handleBlur,
  name,
  enableFilterOption,
  disabled,
  renderCustomLabel,
  renderSelectedValue,
  disableSortOptions,
  forcePopupIcon = true,
  CustomPopperComponent,
  gutterTop,
  endIcon,
  clearable = true,
}: Readonly<AsyncFilterSelectProps<T>>) {
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const CustomHandleBlur = (e: React.FocusEvent<HTMLDivElement, Element>) => {
    onSearch?.("");
    handleBlur?.(e);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (_: any, newInputValue: string, reason: string) => {
    if (reason === "input") {
      onSearch?.(newInputValue);
    }
  };

  return (
    <form autoComplete="off">
      <Flex
        {...flexProps}
        flexDirection={flexProps?.flexDirection ?? "column"}
        height={CHART_DIMENSIONS.controlHeight + "!important"}
      >
        <Autocomplete
          style={{
            marginTop: !!gutterTop ? "30px" : undefined,
          }}
          autoHighlight={false}
          autoSelect={false}
          forcePopupIcon={forcePopupIcon}
          disabled={disabled}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          disableClearable={!clearable}
          fullWidth
          size="small"
          onBlur={CustomHandleBlur}
          PopperComponent={CustomPopperComponent}
          value={value}
          slotProps={{ popper: { sx: { zIndex: 99999 } } }}
          onInputChange={handleInputChange}
          onChange={(_, val) => {
            onSearch?.("");
            return onChange(val);
          }}
          renderOption={(props, option) => (
            <ListItem key={option.value} {...props} sx={DEFAULT_FONTSIZE}>
              <Radio
                checked={
                  value?.[formatKeys.val ?? "value"] ===
                  option?.[formatKeys.val ?? "value"]
                }
                size="small"
                sx={{ p: 0, mr: 1 }}
              />
              {renderCustomLabel
                ? renderCustomLabel(option)
                : option[formatKeys.label]}
            </ListItem>
          )}
          filterOptions={enableFilterOption ? (opt) => opt : undefined}
          isOptionEqualToValue={(option, value) =>
            option?.[formatKeys.val] === value?.[formatKeys.val]
          }
          getOptionLabel={(option) => option?.[formatKeys.label]}
          popupIcon={endIcon}
          options={
            (disableSortOptions
              ? options?.filter(
                  (a) => value?.[formatKeys.val ?? "value"] !== a?.value
                )
              : options?.sort((a, b) =>
                  a?.[formatKeys.label ?? "label"]?.localeCompare(
                    b?.[formatKeys.label ?? "label"]
                  )
                )
            )?.filter(
              (a) =>
                value?.[formatKeys.val ?? "value"] !==
                a?.[formatKeys.val ?? "value"]
            ) ?? []
          }
          loading={loading}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: CHART_DIMENSIONS.controlHeight,
              minHeight: CHART_DIMENSIONS.controlHeight,
              padding: "0 8px !important",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiOutlinedInput-input": {
              height: "100% !important",
              padding: "0 !important",
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
            },
            "& .MuiInputBase-root": {
              height: CHART_DIMENSIONS.controlHeight,
            },
            "& .MuiFormControl-root": {
              height: CHART_DIMENSIONS.controlHeight,
            },
            ...(endIcon && {
              "& .MuiAutocomplete-popupIndicator": {
                transform: "none",
              },
            }),
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={inputLabel(label, required)}
              InputLabelProps={{ shrink: true }}
              name={name}
              error={!!error && !!error}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
              onBlur={handleBlur}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                startAdornment:
                  renderSelectedValue && value ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      {renderSelectedValue(value)}
                    </Box>
                  ) : undefined,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />

        {!!error && (
          <Text fontSize={"13px"} variant="caption" color="error">
            {error}
          </Text>
        )}
      </Flex>
    </form>
  );
}

export default AsyncFilterSelect;
